import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import type { PreswitchLightResponse } from "../types/addServiceTypes";

type PslData = PreswitchLightResponse["data"];

export type PslEntry =
  | { state: "loading" }
  | { state: "ready"; data: PslData }
  | { state: "error"; message: string };

/**
 * Preswitch Light for the EANs of ONE selected group.
 *
 * WHY THIS IS ITS OWN HOOK, and why the cache is keyed on company A alone:
 *
 * - The response is the ONLY source of `checkExistingEan` (who else holds the point),
 *   `sdpAtriasConfig`, `typeOfMeter`, the running-process fields and `addPsl.status`.
 *   Eligibility therefore cannot be decided from the portfolio alone, and a ticked row
 *   can legitimately flip to blocked once its PSL lands.
 * - The whole response is posted VERBATIM as the `POST /sdp/addSdps/{B}` body, so
 *   without it a point cannot be transferred at all.
 * - 🔴 `ADD_PSL` HAS A SIDE EFFECT. The call is a synchronous REQUEST to Atrias; the
 *   write is ours — `addPslData` INSERTs into `mig6_messages_preswitchligt`. Which
 *   company that row is stored under does NOT matter (product ruling: only the data
 *   matters). What matters is that the round trip is real and already happened, so
 *   deselecting must NOT evict the entry and re-ticking must not repeat it. Entries
 *   are retained for the lifetime of the company selection and dropped only when the
 *   company changes or the component unmounts.
 * - Called with company A (`sourceCompanyId`) — the company that HOLDS the point and
 *   whose SDP actually exists. Every other call on this path uses company B.
 */
export const useGroupPreswitch = (sourceCompanyId: number | undefined, token: string | undefined) => {
  const [entries, setEntries] = useState<Map<string, PslEntry>>(new Map());
  /** In-flight aborts, so a company change cannot land a stale response. */
  const inFlight = useRef<Map<string, AbortController>>(new Map());
  /**
   * 🔴 EANs already ATTEMPTED, successfully or not. This is a different question
   * from `inFlight` (which is only about concurrency) and from `entries` (which is
   * render state), and conflating them is what made a failed lookup retry forever:
   * `ensure` skipped only `ready`, so an `error` entry was re-fetched on every
   * render that re-ran the effect. Each attempt is a real Atrias round trip plus an
   * INSERT of ours, so a transient Atrias blip became a self-inflicted request flood
   * (measured at ~476 requests in 300ms).
   *
   * A failure is therefore TERMINAL until the user asks again. Held in a ref, not
   * state, so `ensure` does not change identity when it mutates — an `ensure` that
   * depended on `entries` was itself a second loop source, since every fetch
   * rewrote `entries` and thus re-fired the effect that calls it.
   */
  const attempted = useRef<Set<string>>(new Set());

  // COMPANY A CHANGED → the whole cache belongs to the previous company. Drop it and
  // abort anything still running. Same hazard EndContractManager documents: the header
  // switcher is reachable at every step, and a retained response would describe a
  // company the user is no longer standing in.
  useEffect(() => {
    inFlight.current.forEach((controller) => controller.abort());
    inFlight.current = new Map();
    attempted.current = new Set();
    setEntries(new Map());
  }, [sourceCompanyId]);

  /**
   * UNMOUNT → abort anything still outbound.
   *
   * 🔴 Reads `inFlight.current` AT CLEANUP TIME, not at mount. Capturing it in the
   * effect body (`const controllers = inFlight.current`) with `[]` deps binds the
   * Map that existed at mount — but the company-change effect above REASSIGNS
   * `inFlight.current = new Map()`, so after any company switch the cleanup aborted
   * an orphaned Map while every later fetch registered in the new one. The
   * observable consequence: switch company, select an EAN under B (a real ADD_PSL
   * write), navigate away before it resolves, and the write was never aborted —
   * silently defeating the only thing this effect exists to do.
   */
  useEffect(
    () => () => {
      inFlight.current.forEach((controller) => controller.abort());
      inFlight.current.clear();
    },
    []
  );

  const fetchOne = useCallback(
    async (ean: string) => {
      if (!sourceCompanyId || !token) return;
      if (inFlight.current.has(ean)) return;
      // Marked BEFORE the request goes out, so a re-render during the round trip
      // cannot queue a second write for the same point.
      attempted.current.add(ean);

      const controller = new AbortController();
      inFlight.current.set(ean, controller);
      setEntries((prev) => new Map(prev).set(ean, { state: "loading" }));
      try {
        const res = await axios.get<PreswitchLightResponse>(
          `${import.meta.env.VITE_API_SERVICES}/api/v1/services/preswitchLigt/${sourceCompanyId}/ADD_PSL/${ean}`,
          { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
        );
        setEntries((prev) => new Map(prev).set(ean, { state: "ready", data: res.data.data }));
      } catch (err) {
        if (axios.isCancel(err)) return;
        setEntries((prev) =>
          new Map(prev).set(ean, {
            state: "error",
            message: axios.isAxiosError(err)
              ? ((err.response?.data as { message?: string })?.message ?? err.message)
              : "Preswitch lookup failed",
          })
        );
      } finally {
        inFlight.current.delete(ean);
      }
    },
    [sourceCompanyId, token]
  );

  /**
   * Ensure every EAN of the group has been looked up. Sequential: each call is a real
   * Atrias SOAP round trip, and a group is at most two points.
   */
  /**
   * Look up every EAN of the group that has not been attempted yet. Sequential:
   * each call is a real Atrias round trip and a group is at most two points.
   *
   * Deliberately keyed on `attempted` (a ref) rather than on `entries` (state), so
   * this callback keeps a STABLE identity. A caller effect that lists it as a
   * dependency must not be re-armed by the very fetches it triggered.
   */
  const ensure = useCallback(
    async (eans: string[]) => {
      for (const ean of eans) {
        if (attempted.current.has(ean)) continue;
        await fetchOne(ean);
      }
    },
    [fetchOne]
  );

  /**
   * The ONLY path back from a terminal failure, and it is user-initiated by
   * construction — clearing the attempted mark is not something a render can do.
   */
  const retry = useCallback(
    (ean: string) => {
      attempted.current.delete(ean);
      setEntries((prev) => {
        const next = new Map(prev);
        next.delete(ean);
        return next;
      });
      void fetchOne(ean);
    },
    [fetchOne]
  );

  return { entries, ensure, retry };
};
